import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from "@mui/material";
import NavBar from "../navbar";
import { Link } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const Balances = () => {
    const [orderBy, setOrderBy] = useState("updated_at");
    const [order, setOrder] = useState("desc");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) {
            setError(validate);
            return;
        }

        const getBalances = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + "/admin/balances", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} ${response.statusText}`);
                }
                const jsonData = await response.json();
                setData(jsonData);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            }
        };

        getBalances();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const getNestedValue = (obj, path) => path.split(".").reduce((acc, part) => acc?.[part], obj);

    const sortData = (rows) =>
        [...rows].sort((a, b) => {
            const valueA = getNestedValue(a, orderBy);
            const valueB = getNestedValue(b, orderBy);
            if (typeof valueA === "string" && typeof valueB === "string") {
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }
            if (typeof valueA === "number" && typeof valueB === "number") {
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }
            const dateA = new Date(valueA);
            const dateB = new Date(valueB);
            return order === "asc" ? dateA - dateB : dateB - dateA;
        });

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <>
                <NavBar />
                <div style={{ textAlign: "center", padding: "20px" }}>No balances found</div>
            </>
        );
    }

    const sortedRows = sortData(data);

    return (
        <>
            <NavBar />
            <TableContainer component={Paper} style={{ marginTop: "20px" }}>
                <Table sx={{ minWidth: 650 }} aria-label="balances table">
                    <TableHead>
                        <TableRow>
                            <SortableTableCell column="id" label="ID" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="user.email" label="User Email" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="plan.name" label="Plan" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="points" label="Points" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="purchase_credits" label="Topup" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="token_count" label="Tokens" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="subscription_id" label="Subscription" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="expires_at" label="Expires At" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="updated_at" label="Updated At" orderBy={orderBy} order={order} handleSort={handleSort} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <Link to={`/balances/${row.id}/edit`} style={{ textDecoration: "none", color: "#007bff" }}>
                                        {row.id}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    <Link to={`/users/${row.user.id}/edit`} style={{ textDecoration: "none", color: "#007bff" }}>
                                        {row.user.email}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    <Link to={`/plans/${row.plan.id}/edit`} style={{ textDecoration: "none", color: "#007bff" }}>
                                        {row.plan.name}
                                    </Link>
                                </TableCell>
                                <TableCell>{row.points}</TableCell>
                                <TableCell>{row.purchase_credits}</TableCell>
                                <TableCell>{row.token_count}</TableCell>
                                <TableCell style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{row.subscription_id || "—"}</TableCell>
                                <TableCell>{formatUtc(row.expires_at)}</TableCell>
                                <TableCell>{formatUtc(row.updated_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const SortableTableCell = ({ column, label, orderBy, order, handleSort }) => (
    <TableCell onClick={() => handleSort(column)} style={{ cursor: "pointer" }}>
        {label} {orderBy === column && (order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
    </TableCell>
);

export default Balances;
